import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Grid, Button, Paper, Tooltip } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { ConnectedConfiguratorScreen } from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Screen';
import CustomThemeProvider from '../ThemeProvider';
import AmountPriceContainer from '../Containers/AmountPriceContainer';

import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import { doCalculation } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import {
  setWehaLines,
  setWehaGlobalSettings,
  setWehaSelectedLineIds,
  getFontData,
} from '../Reducers/Configurator/Actions';
import type { WehaLine, WehaGlobalSettings } from '../Reducers/Configurator/types';
import { getSpecialCharactersForFont, type FontDataEntry } from '../Reducers/Configurator/fontData';
import { selectWehaFontData } from '../Reducers/Configurator/Selectors';
import {
  OpentypeCanvas,
  type OpentypeCanvasHandle,
  REAL_SIZE_MM,
  WEHA_MEASUREMENT_UI_PX,
} from '../Components/OpentypeCanvas/OpentypeCanvas';
import { createRealSizeA4TiledPdf } from '../utils/realSizePdf';
import GeneralTextSettings from '../Components/Configurator/GeneralTextSettings';
import SelectedLineSettings from '../Components/Configurator/SelectedLineSettings';
import SpecialCharsModal from '../Components/Configurator/SpecialCharsModal';
import CustomHeader from '../Components/Configurator/CustomHeader';
import { PreviewOnStoneOverlay } from '../Components/PreviewOnStone/PreviewOnStoneOverlay';
import { SelectBackgroundDialog } from '../Components/PreviewOnStone/SelectBackgroundDialog';

import { useTheme } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { FONT_TYPE_SINGLE_LETTERS } from '../constants/fontTypes';
import { CASING_GROSS, CASING_GEMISCHT } from '../constants/casing';
import { getDefaultLinePlaceholderText } from '../constants/lineText';
import { getMaterialColor } from '../utils/materialColors';
import { PREVIEW_SCALE } from '../utils/fontPathScaling';

/** Defaults when no font data is loaded yet or API failed; actual values come from API font data when available. */
const defaultGlobalSettings = {
  fontFamily: 'antares',
  fontType: FONT_TYPE_SINGLE_LETTERS,
  material: 'bronze',
  alignment: 'left' as const,
  dowelTemplate: false,
  extraLongDowel: false,
};

const MAX_LINES = 10;
/** Default canvas size before OpentypeCanvas reports actual size. */
const DEFAULT_CANVAS_SIZE = { w: 1000, h: 800 };

function createNewLine(
  fontData: FontDataEntry[],
  globalSettings: typeof defaultGlobalSettings,
  canvasSize: { w: number; h: number },
  existingLines: WehaLine[]
): WehaLine {
  const font = fontData.find((f) => f.identifier === globalSettings.fontFamily) ?? fontData[0];
  const firstSize = font?.sizes?.find((s) => !s.numberSizeOnly) ?? font?.sizes?.[0];
  const color = getMaterialColor(globalSettings.material ?? '');
  const { left, top } = getNewLinePosition(existingLines, globalSettings, canvasSize, false);
  return {
    id: `line-${Date.now()}`,
    text: getDefaultLinePlaceholderText(),
    fontSize: firstSize?.value ?? '30/16',
    casing: CASING_GEMISCHT,
    left,
    top,
    fill: color,
    charSpacing: 0,
    vertical: false,
  };
}
/** Top margin for the first line so content above the cap (diacritics) is not clipped by the viewport. */
const FIRST_LINE_TOP_MARGIN = 7;
/** Nominal cap height in px for new line (30mm × PREVIEW_SCALE 1.2). */
const NOMINAL_NEW_LINE_HEIGHT_PX = 30 * PREVIEW_SCALE;

function getNominalHeightPx(fontSize: string): number {
  return fontSize === '40mm' ? 40 * 1.2 : 30 * 1.2;
}

function alignmentOffsetPx(alignment: 'left' | 'center' | 'right', width: number): number {
  if (alignment === 'left') return 0;
  if (alignment === 'right') return width;
  return width / 2;
}

/** Canvas X of box origin — matches OpentypeCanvas `renderX` (anchor minus alignment offset for horizontal lines). */
function lineRenderLeftPx(line: WehaLine, alignment: 'left' | 'center' | 'right'): number {
  const w = line.width ?? 0;
  if (line.vertical) return line.left ?? 0;
  return (line.left ?? 0) - alignmentOffsetPx(alignment, w);
}

/** Lowest Y (canvas px) that still clears width ruler + label below the line’s box. */
function lineLayoutBottomPx(line: WehaLine): number {
  const top = line.top ?? 0;
  const h = line.height ?? getNominalHeightPx(line.fontSize);
  const m = WEHA_MEASUREMENT_UI_PX;
  return top + h + m.gapBelowBox + m.widthRulerDepth;
}

/** Rightmost X occupied by the line including vertical height ruler when drawn on the right. */
function lineLayoutRightPx(line: WehaLine, alignment: 'left' | 'center' | 'right'): number {
  const w = line.width ?? 0;
  const rl = lineRenderLeftPx(line, alignment);
  if (!line.vertical) return rl + w;
  const m = WEHA_MEASUREMENT_UI_PX;
  const rulerOnRight = rl < m.safetyLeftMargin;
  if (rulerOnRight) return rl + w + m.gapBelowBox + m.heightRulerSideExtent;
  return rl + w;
}

/** Leftmost X including vertical height ruler when drawn on the left. */
function lineLayoutLeftPx(line: WehaLine, alignment: 'left' | 'center' | 'right'): number {
  const w = line.width ?? 0;
  const rl = lineRenderLeftPx(line, alignment);
  if (!line.vertical) return rl;
  const m = WEHA_MEASUREMENT_UI_PX;
  const rulerOnRight = rl < m.safetyLeftMargin;
  if (!rulerOnRight) return rl - m.gapBelowBox - m.heightRulerSideExtent;
  return rl;
}

/**
 * Returns { left, top } for a new line.
 *
 * `newLineWillBeVertical`: `addLine()` always adds horizontal lines first; pass false. Pass true only if the new row
 * is created as vertical (e.g. future duplicate flow).
 */
function getNewLinePosition(
  lines: WehaLine[],
  globalSettings: { alignment?: 'left' | 'center' | 'right' },
  canvasSize: { w: number; h: number },
  newLineWillBeVertical: boolean
): { left: number; top: number } {
  if (lines.length === 0) return { left: 0, top: FIRST_LINE_TOP_MARGIN };

  const lastAdded = lines[0];
  const alignment = globalSettings.alignment ?? 'left';
  const lastLeft = lastAdded.left ?? 0;
  const newHeight = NOMINAL_NEW_LINE_HEIGHT_PX;
  const otherLines = lines.slice(1);
  const gapStack = WEHA_MEASUREMENT_UI_PX.stackGap;

  const overlapsX = (newLeft: number, newWidth: number, line: WehaLine) => {
    const w = line.width ?? 0;
    const l = lineRenderLeftPx(line, alignment);
    return newLeft < l + w && newLeft + newWidth > l;
  };

  /** 2D intersect: provisional horizontal band while text is still empty (caps typical row width). */
  const horizontalBandOverlapsExisting = (left0: number, top0: number, excludeId: string | number) => {
    const probeW = Math.max(1, Math.min(480, canvasSize.w - left0));
    const right0 = left0 + probeW;
    return lines.some((line) => {
      if (String(line.id) === String(excludeId)) return false;
      const l0 = lineLayoutLeftPx(line, alignment);
      const r0 = lineLayoutRightPx(line, alignment);
      const t = line.top ?? 0;
      const b = lineLayoutBottomPx(line);
      return left0 < r0 && right0 > l0 && top0 < b && top0 + newHeight > t;
    });
  };

  if (!lastAdded.vertical) {
    const lastLayoutBottom = lineLayoutBottomPx(lastAdded);
    const newLeft = lastLeft;
    const newTop = lastLayoutBottom + gapStack;
    const newWidth = 0;

    const lineBelow = otherLines.find((line) => {
      const top = line.top ?? 0;
      return top >= lastLayoutBottom && overlapsX(newLeft, Math.max(newWidth, 1), line);
    });
    if (lineBelow) {
      const gap = (lineBelow.top ?? 0) - lastLayoutBottom;
      if (gap < gapStack + newHeight) return { left: 0, top: 0 };
    }
    return { left: newLeft, top: newTop };
  }

  // Latest line is vertical; new line is horizontal — sit to the right of that vertical, same top (not under it).
  if (!newLineWillBeVertical) {
    const newLeft = lineLayoutRightPx(lastAdded, alignment) + gapStack;
    const newTop = lastAdded.top ?? 0;
    if (newLeft > canvasSize.w - 20) return { left: 0, top: 0 };
    if (horizontalBandOverlapsExisting(newLeft, newTop, lastAdded.id)) {
      return { left: 0, top: 0 };
    }
    return { left: newLeft, top: newTop };
  }

  const onlyVertical = lines.every((l) => l.vertical);
  if (onlyVertical) {
    const newLeft = lineLayoutRightPx(lastAdded, alignment) + gapStack;
    const newTop = lastAdded.top ?? 0;
    if (newLeft > canvasSize.w - 20) return { left: 0, top: 0 };
    const nominalNextW = getNominalHeightPx(lastAdded.fontSize);
    const overlaps = otherLines.some((line) => {
      const l0 = lineLayoutLeftPx(line, alignment);
      const r0 = lineLayoutRightPx(line, alignment);
      const t = line.top ?? 0;
      const b = lineLayoutBottomPx(line);
      const newR = newLeft + Math.max(nominalNextW, 1);
      return newLeft < r0 && newR > l0 && newTop < b && newTop + newHeight > t;
    });
    if (overlaps) return { left: 0, top: 0 };
    return { left: newLeft, top: newTop };
  }

  const newLeft = lineLayoutRightPx(lastAdded, alignment) + gapStack;
  const newTop = lastAdded.top ?? 0;
  if (newLeft > canvasSize.w - 20) return { left: 0, top: 0 };
  const nominalNextW = getNominalHeightPx(lastAdded.fontSize);
  const overlaps = lines.slice(1).some((line) => {
    const l0 = lineLayoutLeftPx(line, alignment);
    const r0 = lineLayoutRightPx(line, alignment);
    const t = line.top ?? 0;
    const b = lineLayoutBottomPx(line);
    const newR = newLeft + Math.max(nominalNextW, 1);
    return newLeft < r0 && newR > l0 && newTop < b && newTop + newHeight > t;
  });
  if (overlaps) return { left: 0, top: 0 };
  return { left: newLeft, top: newTop };
}

const CustomDesignerScreen = (
  props: Record<string, unknown> & { printToPdfRef?: React.MutableRefObject<(() => Promise<void>) | null> }
) => {
  const { printToPdfRef } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const w: Record<string, unknown> = ((theme as { weha?: unknown }).weha as Record<string, unknown>) || {};

  const fontData = useSelector(selectWehaFontData);

  React.useEffect(() => {
    getFontData(dispatch);
  }, []);

  const lines = useSelector(
    (state: { configurator?: { configuration?: { customdata?: { wehaLines?: WehaLine[] } } } }) =>
      (state.configurator?.configuration?.customdata?.wehaLines ?? []) as WehaLine[]
  );

  const globalSettings = useSelector(
    (state: {
      configurator?: {
        configuration?: { customdata?: { wehaGlobalSettings?: typeof defaultGlobalSettings } };
      };
    }) =>
      ({
        ...defaultGlobalSettings,
        ...state.configurator?.configuration?.customdata?.wehaGlobalSettings,
      }) as typeof defaultGlobalSettings
  );

  //@todo: Might come from backend in the future, then remove shouldSeedGlobalSettings handling.
  // When no configuration was loaded, wehaGlobalSettings is never in customData. Seed defaults so backend/calculation get them.
  const shouldSeedGlobalSettings = useSelector(
    (state: {
      configurator?: {
        configuration?: { customdata?: { wehaGlobalSettings?: unknown } };
      };
    }) =>
      state.configurator?.configuration != null &&
      state.configurator.configuration.customdata?.wehaGlobalSettings === undefined
  );

  const shouldSeedDefaultLine = useSelector(
    (state: {
      configurator?: {
        configuration?: { customdata?: { wehaLines?: WehaLine[] } };
      };
    }) =>
      state.configurator?.configuration != null &&
      state.configurator.configuration.customdata?.wehaLines === undefined
  );

  React.useEffect(() => {
    if (shouldSeedGlobalSettings) {
      dispatch(setWehaGlobalSettings(defaultGlobalSettings));
      dispatch(doCalculation());
    }
  }, [dispatch, shouldSeedGlobalSettings]);

  const selectedLineId = useSelector(
    (state: { configurator?: { configuration?: { customdata?: { wehaSelectedLineId?: string | null } } } }) =>
      state.configurator?.configuration?.customdata?.wehaSelectedLineId ?? null
  );

  const selectedLineIds = useSelector(
    (state: {
      configurator?: {
        configuration?: {
          customdata?: { wehaSelectedLineIds?: string[]; wehaSelectedLineId?: string | null };
        };
      };
    }) => {
      const ids = state.configurator?.configuration?.customdata?.wehaSelectedLineIds;
      const single = state.configurator?.configuration?.customdata?.wehaSelectedLineId;
      return ids?.length ? ids : single ? [single] : [];
    }
  );

  const displayNetPrices = useSelector(
    (state: { configurator?: { displayNetPrices?: boolean } }) =>
      state.configurator?.displayNetPrices ?? false
  );

  const opentypeCanvasRef = React.useRef<OpentypeCanvasHandle>(null);
  const [specialCharsModalOpen, setSpecialCharsModalOpen] = React.useState(false);
  const specialCharsModalOpenRef = React.useRef(false);
  const closedAfterCharSelectionRef = React.useRef(false);
  const [specialCharsCursorPosition, setSpecialCharsCursorPosition] = React.useState(0);
  const [canvasSize, setCanvasSize] = React.useState(DEFAULT_CANVAS_SIZE);
  /** After clearing BeispielText on focus, do not remove the line on blur (empty string only). */
  const skipRemoveEmptyLineOnBlurRef = React.useRef(false);
  const [previewBackgroundImageUrl, setPreviewBackgroundImageUrl] = React.useState<string | null>(null);
  const [previewOverlayOpen, setPreviewOverlayOpen] = React.useState(false);
  const [selectBackgroundDialogOpen, setSelectBackgroundDialogOpen] = React.useState(false);
  const [crossingSizeDialogOpen, setCrossingSizeDialogOpen] = React.useState(false);

  const selectedLine = React.useMemo(
    () =>
      (selectedLineId
        ? ((lines.find((l: WehaLine) => String(l.id) === String(selectedLineId)) as WehaLine | undefined) ??
          null)
        : null) as WehaLine | null,
    [lines, selectedLineId]
  );

  const hasFontData = fontData.length > 0;

  React.useEffect(() => {
    if (!hasFontData || !shouldSeedDefaultLine) return;
    const newLine = createNewLine(fontData, globalSettings, canvasSize, []);
    dispatch(setWehaGlobalSettings({ ...globalSettings, alignment: 'left' }));
    dispatch(setWehaLines([newLine]));
    dispatch(setWehaSelectedLineIds([String(newLine.id)]));
    dispatch(doCalculation());
  }, [dispatch, hasFontData, shouldSeedDefaultLine, fontData, globalSettings, canvasSize]);

  const addLine = () => {
    if (!hasFontData) return;
    if (lines.length >= MAX_LINES) return;
    if (lines.length === 0) {
      dispatch(setWehaGlobalSettings({ ...globalSettings, alignment: 'left' }));
    }
    const newLine = createNewLine(fontData, globalSettings, canvasSize, lines);
    dispatch(setWehaLines([newLine, ...lines]));
    dispatch(setWehaSelectedLineIds([String(newLine.id)]));
  };

  const removeLine = (id: string | number) => {
    const nextLines = lines.filter((l) => String(l.id) !== String(id));
    dispatch(setWehaLines(nextLines));
    if (String(selectedLineId) === String(id)) {
      dispatch(setWehaSelectedLineIds(nextLines.length ? [String(nextLines[0].id)] : []));
    } else if (selectedLineIds.includes(String(id))) {
      dispatch(setWehaSelectedLineIds(selectedLineIds.filter((sid) => sid !== String(id))));
    }
  };

  const removeLineIfEmptyOnBlur = (id: string | number) => {
    if (skipRemoveEmptyLineOnBlurRef.current) {
      skipRemoveEmptyLineOnBlurRef.current = false;
      return;
    }
    removeLine(id);
  };

  const updateLine = (id: string | number, key: string, value: unknown) => {
    const newLines = lines.map((line: WehaLine) => (line.id === id ? { ...line, [key]: value } : line));
    dispatch(setWehaLines(newLines));
  };

  const handleCasingChange = (lineId: string | number, newValue: string) => {
    const line = lines.find((l: WehaLine) => String(l.id) === String(lineId)) as WehaLine | undefined;
    if (!line) return;
    if (newValue === CASING_GROSS) {
      dispatch(
        setWehaLines(
          lines.map((l: WehaLine) =>
            l.id === lineId
              ? {
                  ...l,
                  mixedCaseSnapshot: l.text,
                  text: (l.text || '').toUpperCase(),
                  casing: CASING_GROSS,
                }
              : l
          )
        )
      );
    } else {
      dispatch(
        setWehaLines(
          lines.map((l: WehaLine) =>
            l.id !== lineId
              ? l
              : {
                  ...l,
                  text: l.mixedCaseSnapshot ?? l.text,
                  casing: CASING_GEMISCHT,
                  mixedCaseSnapshot: undefined,
                }
          )
        )
      );
    }
  };

  const updateGlobalSettings = (changes: Partial<WehaGlobalSettings>) => {
    dispatch(setWehaGlobalSettings(changes));
  };

  const handleSelectLineIds = (ids: string[]) => {
    if (ids.length === 0 && specialCharsModalOpenRef.current) return;
    dispatch(setWehaSelectedLineIds(ids));
  };

  const handleSpecialCharsButtonMouseDown = () => {
    specialCharsModalOpenRef.current = true;
  };

  const handleOpenSpecialCharsModal = (cursorPosition: number) => {
    setSpecialCharsCursorPosition(cursorPosition);
    setSpecialCharsModalOpen(true);
  };

  const handleSpecialCharSelect = (char: string) => {
    if (selectedLine == null) return;
    closedAfterCharSelectionRef.current = true;
    specialCharsModalOpenRef.current = false;
    const text = selectedLine.text ?? '';
    const pos = Math.max(0, Math.min(specialCharsCursorPosition, text.length));
    const newText = text.slice(0, pos) + char + text.slice(pos);
    dispatch(setWehaLines(lines.map((l) => (l.id === selectedLine.id ? { ...l, text: newText } : l))));
    setSpecialCharsModalOpen(false);
  };

  const handleLinesChange = (newLines: WehaLine[]) => {
    dispatch(setWehaLines(newLines));
  };

  const ARROW_KEY_STEP = 5;
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (previewOverlayOpen || selectBackgroundDialogOpen || specialCharsModalOpen || crossingSizeDialogOpen)
        return;
      const key = e.key;
      if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight') return;
      if (selectedLineIds.length === 0) return;
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      const isInput = tag === 'input' || tag === 'textarea' || (active as HTMLElement)?.isContentEditable;
      if (isInput) return;
      e.preventDefault();
      const dx = key === 'ArrowLeft' ? -ARROW_KEY_STEP : key === 'ArrowRight' ? ARROW_KEY_STEP : 0;
      const dy = key === 'ArrowUp' ? -ARROW_KEY_STEP : key === 'ArrowDown' ? ARROW_KEY_STEP : 0;
      const selectedSet = new Set(selectedLineIds);
      const newLines = lines.map((line) => {
        if (!selectedSet.has(String(line.id))) return line;
        const left = (line.left ?? 0) + dx;
        const top = (line.top ?? 0) + dy;
        return {
          ...line,
          left: Math.max(0, Math.min(canvasSize.w, left)),
          top: Math.max(0, Math.min(canvasSize.h, top)),
        };
      });
      dispatch(setWehaLines(newLines));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    lines,
    selectedLineIds,
    dispatch,
    canvasSize.w,
    canvasSize.h,
    previewOverlayOpen,
    selectBackgroundDialogOpen,
    specialCharsModalOpen,
    crossingSizeDialogOpen,
  ]);

  const handlePreviewOnStone = () => {
    if (previewBackgroundImageUrl) {
      setPreviewOverlayOpen(true);
    } else {
      setPreviewOverlayOpen(true);
      setSelectBackgroundDialogOpen(true);
    }
  };

  const getPreviewFontSnapshot = () => {
    return (
      opentypeCanvasRef.current?.getSnapshotWithContentBounds?.('image/png') ??
      opentypeCanvasRef.current
        ?.getSnapshot('image/png')
        .then((dataUrl) => ({ dataUrl, contentBounds: null })) ??
      Promise.resolve({ dataUrl: '', contentBounds: null })
    );
  };

  const handleBackgroundSelected = (dataUrl: string) => {
    setPreviewBackgroundImageUrl(dataUrl);
    setSelectBackgroundDialogOpen(false);
    setPreviewOverlayOpen(true);
  };

  const handleDownloadRealSizePdf = React.useCallback(async () => {
    // Utilize dynamic SVG extraction avoiding the hardcoded bounding box
    const result = opentypeCanvasRef.current?.getRealSizeSvg?.();
    if (!result || !result.svg) return;
    try {
      const blob = await createRealSizeA4TiledPdf(result.svg, result.widthMm, result.heightMm, {
        lineRegionsMm: result.lineRegionsMm,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'schriftzug_vorschau.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF export failed', e);
    }
  }, []);

  React.useEffect(() => {
    if (printToPdfRef) printToPdfRef.current = handleDownloadRealSizePdf;
    return () => {
      if (printToPdfRef) printToPdfRef.current = null;
    };
  }, [printToPdfRef, handleDownloadRealSizePdf]);

  const showHeader = getConf('showHeader', true);
  const APP_BG = w.pageBackground || theme.palette.background.default;

  // Enforces a strict layout block preventing visual blowout before stacking at 960px
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box style={{ minHeight: '100vh', backgroundColor: APP_BG }}>
      {showHeader && props.renderHeader?.()}
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          ...(isMobile
            ? { minHeight: 'calc(100vh - 64px)', overflow: 'visible' }
            : { height: 'calc(100vh - 64px)', overflow: 'hidden' }),
          backgroundColor: APP_BG,
        }}
      >
        <Grid
          container
          style={
            isMobile
              ? { flex: '0 1 auto', minHeight: 0, overflow: 'visible' }
              : { flex: 1, minHeight: 0, overflow: 'hidden' }
          }
        >
          {/* LEFT: Visualization + Preview section below */}
          <Grid
            item
            xs={12}
            md={7}
            lg={8}
            style={{
              position: 'relative',
              backgroundColor: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {/* Canvas: Restored relative container bounding the stage, enforcing a 55vh safe height threshold when stacked dynamically so it doesn't get collapsed on mobile */}
            <Box
              style={{
                flex: 1,
                minHeight: isMobile ? 450 : 0,
                height: isMobile ? '55vh' : undefined,
                position: 'relative',
                overflow: 'hidden',
                padding: 0,
                margin: 24,
                marginBottom: 0,
                borderRadius: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                backgroundColor: w.paperBackground || theme.palette.background.paper,
              }}
            >
              <React.Suspense fallback={<div>{t('weha.loading')}</div>}>
                <OpentypeCanvas
                  ref={opentypeCanvasRef}
                  lines={lines}
                  globalSettings={globalSettings}
                  selectedLineIds={selectedLineIds}
                  onSelectLineIds={handleSelectLineIds}
                  onLinesChange={handleLinesChange}
                  overlayOpen={specialCharsModalOpen || previewOverlayOpen || crossingSizeDialogOpen}
                  onRemoveLine={removeLine}
                  onUpdateLine={updateLine}
                  onCanvasSizeChange={setCanvasSize}
                />
              </React.Suspense>
            </Box>
            {/* Section below canvas: grey background, "Preview on Stone" button */}
            <Box
              style={{
                flexShrink: 0,
                backgroundColor: theme.palette.grey[200] ?? '#e0e0e0',
                padding: '16px 24px',
                margin: 24,
                marginTop: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<span className="icon-eye" />}
                onClick={handlePreviewOnStone}
                style={{
                  borderRadius: w.borderRadius ?? theme.shape.borderRadius,
                  backgroundColor:
                    ((w.buttonSecondary as Record<string, unknown>)?.backgroundColor as string) ||
                    theme.palette.background.paper,
                  padding: '10px 24px',
                  fontWeight: ((w.buttonSecondary as Record<string, unknown>)?.fontWeight as number) || 600,
                  color:
                    ((w.buttonSecondary as Record<string, unknown>)?.color as string) ||
                    theme.palette.text.primary,
                  borderColor:
                    ((w.buttonSecondary as Record<string, unknown>)?.borderColor as string) ||
                    theme.palette.divider,
                  textTransform: 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                }}
              >
                {t('weha.button.previewOnStone')}
              </Button>
            </Box>
          </Grid>

          {/* RIGHT: Control Panel */}
          <Grid
            item
            xs={12}
            md={5}
            lg={4}
            style={{
              display: 'flex',
              flexDirection: 'column',
              ...(isMobile ? { minHeight: 0 } : { height: '100%' }),
              backgroundColor: APP_BG,
            }}
          >
            <Box
              sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}
              style={{
                overflowY: isMobile ? 'visible' : 'auto',
                scrollbarGutter: isMobile ? undefined : 'stable',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                minHeight: isMobile ? 0 : undefined,
              }}
            >
              <Tooltip
                title={t('weha.button.maxLinesReached')}
                disableHoverListener={lines.length < MAX_LINES}
                disableFocusListener={lines.length < MAX_LINES}
              >
                <span style={{ display: 'inline-block', width: '100%' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!hasFontData || lines.length >= MAX_LINES}
                    onClick={addLine}
                    style={{
                      flexShrink: 0,
                      minHeight: 54,
                      height: 54,
                      fontWeight: 600,
                      borderRadius: w.borderRadius ?? theme.shape.borderRadius,
                      fontSize: '1rem',
                    }}
                    startIcon={<span className="icon-plus" style={{ fontSize: '1.2rem', marginRight: 4 }} />}
                  >
                    {t('weha.button.addLine')}
                  </Button>
                </span>
              </Tooltip>

              <SelectedLineSettings
                line={selectedLine}
                globalSettings={globalSettings}
                onUpdateLine={updateLine}
                onCasingChange={handleCasingChange}
                onOpenSpecialCharsModal={handleOpenSpecialCharsModal}
                onRemoveLineIfEmpty={removeLineIfEmptyOnBlur}
                skipRemoveEmptyLineOnBlurRef={skipRemoveEmptyLineOnBlurRef}
                specialCharsModalOpenRef={specialCharsModalOpenRef}
                onSpecialCharsButtonMouseDown={handleSpecialCharsButtonMouseDown}
              />

              <SpecialCharsModal
                open={specialCharsModalOpen}
                onClose={() => {
                  specialCharsModalOpenRef.current = false;
                  setSpecialCharsModalOpen(false);
                  if (closedAfterCharSelectionRef.current) {
                    closedAfterCharSelectionRef.current = false;
                  } else if (
                    selectedLine != null &&
                    (selectedLine.text ?? '').trim() === '' &&
                    !skipRemoveEmptyLineOnBlurRef.current
                  ) {
                    removeLine(selectedLine.id);
                  }
                }}
                characters={getSpecialCharactersForFont(fontData, globalSettings.fontFamily ?? '')}
                fontFamily={globalSettings.fontFamily ?? ''}
                onSelect={handleSpecialCharSelect}
              />

              <GeneralTextSettings
                globalSettings={globalSettings}
                lines={lines}
                onChange={updateGlobalSettings}
                onCrossingSizeDialogOpenChange={setCrossingSizeDialogOpen}
              />
            </Box>

            {/* Footer: same width as tiles above (match scrollbarGutter so width aligns with content) */}
            <Box
              sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, flexShrink: 0 }}
              style={isMobile ? undefined : { overflowY: 'auto', scrollbarGutter: 'stable' }}
            >
              <Paper
                className={`weha-amount-price-paper${displayNetPrices ? ' weha-display-net' : ''}`}
                elevation={0}
                style={{
                  backgroundColor: w.paperBackground || theme.palette.background.paper,
                  padding: '24px',
                  borderRadius: 0,
                  borderTop: `1px solid ${w.borderDefault || theme.palette.divider}`,
                  boxShadow: '0 -1px 3px rgba(0,0,0,0.06)',
                }}
              >
                {/* skip amount price container render if global settings are not seeded to avoid unecessary initial calculation call */}
                {!shouldSeedGlobalSettings && <AmountPriceContainer />}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SelectBackgroundDialog
        open={selectBackgroundDialogOpen}
        onClose={() => {
          setSelectBackgroundDialogOpen(false);
          setPreviewOverlayOpen(false); // Restore canvas interaction!
        }}
        onSelect={handleBackgroundSelected}
      />

      {previewBackgroundImageUrl && (
        <PreviewOnStoneOverlay
          open={previewOverlayOpen}
          onClose={() => setPreviewOverlayOpen(false)}
          backgroundImageUrl={previewBackgroundImageUrl}
          getFontSnapshot={getPreviewFontSnapshot}
          onReplaceBackground={setPreviewBackgroundImageUrl}
        />
      )}

      <ConfiguratorScreenBasics {...props} />
    </Box>
  );
};

CustomDesignerScreen.propTypes = {
  renderHeader: PropTypes.func,
};

const ConfiguratorScreenBasics = ({
  renderError,
  renderAlertMessages,
  renderConfirm,
  renderSaveConfiguration,
  renderSendEmail,
  renderPdfDownload,
  renderLoadConfiguration,
  renderLoader,
}) => (
  <>
    {renderError()}
    {renderAlertMessages()}
    {renderConfirm()}
    {renderSaveConfiguration()}
    {renderSendEmail()}
    {renderPdfDownload()}
    {renderLoadConfiguration()}
    {renderLoader()}
  </>
);

const CustomConfiguratorScreen = (props) => {
  const printToPdfRef = React.useRef<(() => Promise<void>) | null>(null);

  return (
    <ConnectedConfiguratorScreen
      {...props}
      ThemeProvider={CustomThemeProvider}
      DesignerScreen={(designerProps) => (
        <CustomDesignerScreen {...designerProps} printToPdfRef={printToPdfRef} />
      )}
      renderHeader={() => <CustomHeader onPrintToPdfRef={printToPdfRef} />}
      renderVisualization={() => null}
    />
  );
};

export default CustomConfiguratorScreen;
